import debounce from "lodash.debounce"
import React, { useMemo } from "react"
import { trackButtonClick } from "tracking/trackButtonClick"

type ButtonMouseEvent = React.MouseEvent<HTMLButtonElement, MouseEvent>

interface ButtonClickHandlerOptions {
  onClick?: (e: ButtonMouseEvent) => void
  onClickDebounce: number
  trackingName?: string
  trackingId?: string
}

/**
 * The click handler of a `Button`: sends the tracking event, then runs `onClick`.
 * Keyboard-originated clicks (pointerId === -1) are debounced so the Enter key
 * effectively becomes an `onKeyUp` event, which is much simpler than turning the
 * keyboard events into custom/synthetic events whose behaviour differs per
 * browser/OS. Mouse clicks run immediately.
 */
export const useButtonClickHandler = ({
  onClick,
  onClickDebounce,
  trackingName,
  trackingId,
}: ButtonClickHandlerOptions) => {
  const debouncedOnClick = useMemo(
    () => debounce((e: ButtonMouseEvent) => onClick?.(e), onClickDebounce),
    [onClick, onClickDebounce]
  )

  return (e: ButtonMouseEvent) => {
    trackButtonClick({ trackingId: trackingId, buttonName: trackingName })

    if (!onClick) {
      return
    }

    if ((e.nativeEvent as PointerEvent)?.pointerId === -1) {
      debouncedOnClick(e)
    } else {
      onClick(e)
    }
  }
}
